
import { Vue, Component, Prop } from "vue-property-decorator";
import OxyClassTokenActions from "@/oxygen/classes/OxyClassTokenActions.vue";
import { globalStore, uiStore } from "@/store/store-accessor";
import { OxyNestedSelector, OxySelector } from "@/oxygen/classes/lib/types";
import {
  addNestedSelector,
  createNestedSelectorFromName,
  duplicateNestedSelector,
  duplicateSelector,
  getElementOxySelectors,
  isDuplicateNestedSelector,
  isDuplicateSelector,
  removeElementSelector,
  removeNestedSelector,
  removeSelectorFromStore,
  renameNestedSelector,
  renameSelector,
  setElementSelectors,
  toggleSelectorLock
} from "@/oxygen/classes/lib/classes";
import OxyClassTokenNameInput from "@/oxygen/classes/OxyClassTokenNameInput.vue";
import { isValidClassName } from "@/lib/selectors/selectors";
import Icon from "@/components/UI/Icon.vue";

@Component({
  components: {
    Icon,
    OxyClassTokenNameInput,
    OxyClassTokenActions
  }
})
export default class OxyClassToken extends Vue {
  @Prop({ default: false })
  active!: boolean;

  @Prop()
  id!: string;

  @Prop()
  name?: string;

  @Prop({ default: true })
  editable!: boolean;

  @Prop({ default: true })
  creatable!: boolean;

  @Prop({ default: true })
  removable!: boolean;

  @Prop({ default: true })
  lockable!: boolean;

  @Prop({ default: false })
  demo!: boolean;

  @Prop()
  nestedSelector?: OxyNestedSelector;

  @Prop({ default: false })
  hideNestedToken!: boolean;

  renaming = false;
  creating = false;

  createPlaceholder = "Enter name";

  isClassNameInvalid = false;
  classNameErrorMessage = "";

  isNestedSelectorInvalid = false;
  nestedSelectorErrorMessage = "";

  get elementId() {
    return uiStore.activeElementId;
  }

  get element() {
    return uiStore.activeElement;
  }

  get selector() {
    return globalStore.oxySelectors.find(
      (selector: OxySelector) => selector.id === this.id
    );
  }

  get missingSelector() {
    return !this.selector && !this.demo;
  }

  get canViewActions() {
    // Don't show actions for demo selectors or during renaming.
    return this.editable && !this.renaming && !this.demo;
  }

  get renamePrefix() {
    return this.nestedSelector && !this.hideNestedToken
      ? `.${this.selector?.name} `
      : "";
  }

  persistRename(name?: string) {
    if (!this.selector) return;
    this.isClassNameInvalid = false;

    const newName = name?.trim();
    const oldName = this.nestedSelector?.name || this.selector.name;

    if (newName === oldName) {
      this.renaming = false;
      return;
    }

    if (!newName) {
      this.isClassNameInvalid = true;
      this.classNameErrorMessage = "Name must be 1 character or more";
      return;
    }

    if (this.nestedSelector) {
      if (
        isDuplicateNestedSelector(
          newName,
          this.selector.id,
          this.nestedSelector.id
        )
      ) {
        this.isClassNameInvalid = true;
        this.classNameErrorMessage = "Name is already in use";
        return;
      }

      renameNestedSelector(this.selector.id, this.nestedSelector.id, newName);
    } else {
      const isInvalidClassName =
        this.selector.type === "class" && !isValidClassName(`.${newName}`);

      if (isInvalidClassName) {
        this.isClassNameInvalid = true;
        this.classNameErrorMessage = "Invalid class name";
        return;
      }

      if (isDuplicateSelector(newName, this.selector.id)) {
        this.isClassNameInvalid = true;
        this.classNameErrorMessage = "Name is already in use";
        return;
      }

      renameSelector(this.selector.id, newName);
    }

    this.renaming = false;
    this.isClassNameInvalid = false;
    this.classNameErrorMessage = "";
  }

  duplicateSelector() {
    if (!this.selector) return;

    if (this.nestedSelector) {
      const duplicatedSelector = duplicateNestedSelector(
        this.selector.id,
        this.nestedSelector.id
      );

      if (duplicatedSelector) {
        this.editNestedSelector(duplicatedSelector);
      }
    } else {
      const duplicatedSelector = duplicateSelector(this.selector.id);

      if (duplicatedSelector) {
        if (this.element) {
          const selectors = getElementOxySelectors(this.element);
          const ids = selectors.map(s => s.id);
          setElementSelectors(this.elementId!, [...ids, duplicatedSelector.id]);
        }

        this.$emit("select", duplicatedSelector.id);
      }
    }
  }

  removeSelector() {
    removeElementSelector(this.elementId!, this.id);

    this.$emit("clear-select");
  }

  deleteSelector() {
    if (!this.selector) {
      removeElementSelector(this.elementId!, this.id);
      return;
    }

    let message = `Are you sure you want to delete the selector "${this.selector.name}"?`;

    if (this.nestedSelector) {
      message = `Are you sure you want to delete the nested selector ".${this.selector.name} ${this.nestedSelector.name}"?`;
    }

    const shouldDelete = confirm(message);

    if (!shouldDelete) return;

    if (this.nestedSelector) {
      removeNestedSelector(this.selector.id, this.nestedSelector.id);
    } else {
      if (this.elementId) {
        removeElementSelector(this.elementId, this.selector.id);
      }

      removeSelectorFromStore(this.selector.id);
    }

    this.$emit("clear-select");
  }

  editSelectorState(state: string) {
    if (!this.selector) return;

    const maybeSelector = this.selector?.children?.find(s => s.name === state);

    if (!maybeSelector) {
      const newSelector = createNestedSelectorFromName(state);
      addNestedSelector(this.selector.id, newSelector);
    }

    const nestedSelector = this.selector.children?.find(s => s.name === state);

    if (nestedSelector) {
      this.editNestedSelector(nestedSelector);
    }
  }

  editNestedSelector(nestedSelector: OxyNestedSelector) {
    this.activateOrFocus();
    this.$emit("select-nested", nestedSelector.id);
  }

  createNestedSelector(name: string) {
    if (!this.selector) return;
    if (name === this.createPlaceholder) {
      this.isNestedSelectorInvalid = true;
      this.nestedSelectorErrorMessage = "Name must be 1 character or more";
      return;
    }

    const rgx = new RegExp(`^${this.selector.name}`);
    const cleanName = name.replace(rgx, "").trim();

    if (!cleanName) {
      this.isNestedSelectorInvalid = true;
      this.nestedSelectorErrorMessage = "Name must be 1 character or more";
      return;
    }

    if (isDuplicateNestedSelector(cleanName, this.selector.id)) {
      this.isNestedSelectorInvalid = true;
      this.nestedSelectorErrorMessage = "Name is already in use";
      return;
    }

    const nested = createNestedSelectorFromName(cleanName);
    addNestedSelector(this.selector.id, nested);
    this.editNestedSelector(nested);

    this.creating = false;
    this.isNestedSelectorInvalid = false;
    this.nestedSelectorErrorMessage = "";
  }

  cancelNestedSelector() {
    this.creating = false;
    this.isNestedSelectorInvalid = false;
    this.nestedSelectorErrorMessage = "";
  }

  enableRename() {
    if (this.missingSelector) return;
    this.renaming = true;
  }

  cancelRename() {
    this.renaming = false;
    this.isClassNameInvalid = false;
    this.classNameErrorMessage = "";
  }

  enableCreate() {
    this.creating = true;
  }

  activateOrFocus() {
    if (this.renaming) return;
    this.$emit("select", this.selector?.id);
  }

  toggleLock() {
    if (!this.selector) return;

    toggleSelectorLock(this.selector.id);
  }
}
